import { useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { PageHeader, Field, NativeSelect } from "../components/common";
import { api, errMsg, useFetch } from "../lib/api";
import { useT } from "../lib/i18n";

const ROLES = ["admin", "director", "sales_manager", "service_manager", "technician", "viewer"];
const MODULES = [["perm.dashboard", "dashboard", null], ["perm.quotes", "quote:read", "quote:write"], ["perm.publish", null, "quote:publish"],
  ["perm.accept", null, "quote:accept"], ["perm.schedule", "schedule:read", "schedule:write"],
  ["perm.cost", "cost:view", null], ["perm.master", "master:read", "master:write"], ["perm.tax", "tax:read", "tax:write"],
  ["perm.taxValidate", null, "tax:validate"], ["perm.settings", null, "settings:write"], ["perm.audit", "audit:read", null]];

function PermToggles({ f, setF, defaults }) {
  const { t } = useT();
  if (f.role === "admin") return <p className="text-xs text-neutral-500">{t("usr.adminAll")}</p>;
  const cur = new Set(f.perms ?? defaults[f.role] ?? []);
  const flip = (p, on) => { const s = new Set(cur); on ? s.add(p) : s.delete(p); setF({ ...f, perms: [...s] }); };
  const cell = (p) => p ? <Switch data-testid={`perm-${p.replace(":", "-")}`} checked={cur.has(p)} onCheckedChange={(c) => flip(p, c)} /> : <span className="text-neutral-300">—</span>;
  return (
    <div className="rounded border border-neutral-200">
      <table className="dense-table w-full" data-testid="perm-toggles">
        <thead><tr><th>{t("aud.entity")}</th><th>{t("usr.view")}</th><th>{t("usr.editCol")}</th></tr></thead>
        <tbody>{MODULES.map(([k, v, e]) => <tr key={k}><td className="font-medium">{t(k)}</td><td>{cell(v)}</td><td>{cell(e)}</td></tr>)}</tbody>
      </table>
      <div className="flex items-center justify-between px-3 py-2 text-[11px] text-neutral-500">
        <span>{t("usr.adminOnlyNote")}</span>
        <Button size="sm" variant="ghost" data-testid="perm-reset-btn" onClick={() => setF({ ...f, perms: null })}>{t("usr.resetRole")}</Button>
      </div>
    </div>
  );
}

function CountryPicker({ value, countries, onChange }) {
  const { t } = useT();
  const all = value.includes("*");
  const sel = new Set(value);
  const regions = [...new Set(countries.map((c) => c.region))];
  const toggle = (codes, on) => { const s = new Set(sel); s.delete("*"); codes.forEach((c) => (on ? s.add(c) : s.delete(c))); onChange([...s]); };
  return (
    <div className="space-y-3 rounded border border-neutral-200 p-3">
      <label className="flex items-center gap-2 text-xs font-bold"><Checkbox data-testid="user-countries-all" checked={all} onCheckedChange={(c) => onChange(c ? ["*"] : [])} />{t("usr.allCountries")}</label>
      {!all && regions.map((r) => {
        const codes = countries.filter((c) => c.region === r).map((c) => c.code);
        return (
          <div key={r}>
            <label className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-neutral-600">
              <Checkbox checked={codes.every((c) => sel.has(c))} onCheckedChange={(c) => toggle(codes, c)} />{r}
            </label>
            <div className="ml-6 flex flex-wrap gap-3">
              {countries.filter((c) => c.region === r).map((c) => (
                <label key={c.code} className="flex items-center gap-1 text-xs"><Checkbox data-testid={`user-country-${c.code}`} checked={sel.has(c.code)} onCheckedChange={(on) => toggle([c.code], on)} />{c.code}</label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function UsersPage() {
  const { t } = useT();
  const { data, reload } = useFetch("/users");
  const { data: countries } = useFetch("/countries");
  const { data: roleInfo } = useFetch("/roles");
  const [f, setF] = useState(null);
  const save = async () => {
    try {
      const body = { email: f.email, name: f.name, role: f.role, countries: f.countries, active: f.active, password: f.password || null, perms: f.role === "admin" ? null : f.perms ?? null };
      if (f.id) await api.put(`/users/${f.id}`, body); else await api.post("/users", body);
      toast.success(t("common.saved")); setF(null); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title={t("usr.title")} kicker={t("nav.group.admin")}>
        <Button data-testid="new-user-btn" onClick={() => setF({ role: "sales_manager", countries: [], active: true })}><Plus className="h-4 w-4" />{t("usr.new")}</Button>
      </PageHeader>
      <div className="overflow-x-auto rounded-md border border-neutral-200 bg-white">
        <table className="dense-table w-full" data-testid="users-table">
          <thead><tr><th>{t("common.name")}</th><th>{t("cust.email")}</th><th>{t("usr.role")}</th><th>{t("usr.countries")}</th><th>{t("usr.active")}</th><th /></tr></thead>
          <tbody>
            {(data || []).map((u) => (
              <tr key={u.id} className="cursor-pointer" onClick={() => setF({ ...u, password: "" })} data-testid={`user-row-${u.email}`}>
                <td className="font-semibold">{u.name}</td><td>{u.email}</td>
                <td><span className="text-[11px] font-bold uppercase tracking-wider text-[#db0720]">{t(`role.${u.role}`)}</span></td>
                <td>{u.countries.includes("*") ? t("usr.allCountries") : u.countries.join(", ")}</td><td>{u.active ? t("common.yes") : t("common.no")}</td>
                <td className="text-right"><Button size="sm" variant="outline" data-testid={`user-edit-${u.email}`}><Pencil className="h-3.5 w-3.5" />{t("common.edit")}</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!f} onOpenChange={() => setF(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="user-dialog">
          <DialogHeader><DialogTitle>{f?.id ? f.name : t("usr.new")}</DialogTitle></DialogHeader>
          {f && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("common.name")} required><Input data-testid="user-name-input" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
              <Field label={t("cust.email")} required><Input data-testid="user-email-input" disabled={!!f.id} value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
              <Field label={t("usr.role")} required><NativeSelect testid="user-role-select" value={f.role} onChange={(v) => setF({ ...f, role: v, perms: null })} options={ROLES.map((r) => ({ value: r, label: t(`role.${r}`) }))} /></Field>
              <Field label={t("usr.password")} required={!f.id}><Input data-testid="user-password-input" type="password" value={f.password || ""} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
              <Field label={t("usr.active")}><Switch data-testid="user-active-switch" checked={f.active} onCheckedChange={(c) => setF({ ...f, active: c })} /></Field>
              <Field label={t("usr.countries")} required className="sm:col-span-2"><CountryPicker value={f.countries || []} countries={countries || []} onChange={(v) => setF({ ...f, countries: v })} /></Field>
              <Field label={t("usr.perms")} className="sm:col-span-2"><PermToggles f={f} setF={setF} defaults={roleInfo?.defaults || {}} /></Field>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setF(null)}>{t("common.cancel")}</Button><Button data-testid="user-save-btn" onClick={save}>{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
